import apiClient from "services/ApiClient";
import { useQuery } from "@tanstack/react-query";
import { v2ApiPrebuiltPathTemplatesPathByPrebuiltPathId } from "helpers/url/api/v2";
import { TemplateSummary } from "features/Resources/SharedTemplate/models";
import { EmailTemplate, EmailTemplateId } from "features/Resources/EmailTemplate/models";
import { apiPrebuiltEmailTemplateById, apiPrebuiltMeetingTemplateById, apiPrebuiltMessageTemplateById, apiPrebuiltTaskTemplateById } from "helpers/url/api/v2/prebuilt";
import { MessageTemplate, MessageTemplateId } from "features/Resources/MessageTemplate/models";
import { TaskTemplate, TaskTemplateId } from "features/Resources/TaskTemplate/models";
import { MeetingTemplate, MeetingTemplateId } from "features/Resources/MeetingTemplate/models";
import { PrebuiltPathId } from "../PrebuiltPath/models";

export const prebuiltSharedTemplateKeys = {
  all: ["sharedTemplates"] as const,
  lists: () => [...prebuiltSharedTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...prebuiltSharedTemplateKeys.lists(), { filters }] as const,
  byPaths: () => [...prebuiltSharedTemplateKeys.lists(), "paths"] as const,
  byPath: (id: string) => [...prebuiltSharedTemplateKeys.byPaths(), id] as const,
  details: () => [...prebuiltSharedTemplateKeys.all, "detail"] as const,
  detail: (type: "prebuiltEmailTemplate" | "prebuiltMeetingTemplate" | "prebuiltMessageTemplate" | "prebuiltTaskTemplate", id: string) => [...prebuiltSharedTemplateKeys.details(), id] as const,
};

export const useSharedTemplatesByPrebuiltPathIdQuery = (prebuiltPathId: PrebuiltPathId, options = {}) => useQuery<TemplateSummary[]>({
  queryKey: prebuiltSharedTemplateKeys.byPath(prebuiltPathId),
  queryFn: async () => apiClient.get(v2ApiPrebuiltPathTemplatesPathByPrebuiltPathId(prebuiltPathId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!prebuiltPathId && ((options as { enabled?: boolean }).enabled ?? true),
});

export const usePrebuiltEmailTemplateDetailQuery = (prebuiltEmailTemplateId: EmailTemplateId, options = {}) => useQuery<EmailTemplate>({
  queryKey: prebuiltSharedTemplateKeys.detail("prebuiltEmailTemplate", prebuiltEmailTemplateId),
  queryFn: async () => apiClient.get(apiPrebuiltEmailTemplateById(prebuiltEmailTemplateId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!prebuiltEmailTemplateId && ((options as { enabled?: boolean }).enabled ?? true),
});

export const usePrebuiltMeetingTemplateDetailQuery = (prebuiltMeetingTemplateId: MeetingTemplateId, options = {}) => useQuery<MeetingTemplate>({
  queryKey: prebuiltSharedTemplateKeys.detail("prebuiltMeetingTemplate", prebuiltMeetingTemplateId),
  queryFn: async () => apiClient.get(apiPrebuiltMeetingTemplateById(prebuiltMeetingTemplateId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!prebuiltMeetingTemplateId && ((options as { enabled?: boolean }).enabled ?? true),
});

export const usePrebuiltMessageTemplateDetailQuery = (prebuiltMessageTemplateId: MessageTemplateId, options = {}) => useQuery<MessageTemplate>({
  queryKey: prebuiltSharedTemplateKeys.detail("prebuiltMessageTemplate", prebuiltMessageTemplateId),
  queryFn: async () => apiClient.get(apiPrebuiltMessageTemplateById(prebuiltMessageTemplateId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!prebuiltMessageTemplateId && ((options as { enabled?: boolean }).enabled ?? true),
});

export const usePrebuiltTaskTemplateDetailQuery = (prebuiltTaskTemplateId: TaskTemplateId, options = {}) => useQuery<TaskTemplate>({
  queryKey: prebuiltSharedTemplateKeys.detail("prebuiltTaskTemplate", prebuiltTaskTemplateId),
  queryFn: async () => apiClient.get(apiPrebuiltTaskTemplateById(prebuiltTaskTemplateId)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!prebuiltTaskTemplateId && ((options as { enabled?: boolean }).enabled ?? true),
});
